import { useAuth } from '../composables/useAuth';

const API_BASE = import.meta.env.VITE_API_BASE || '/api/v1';

export interface ApiFetchOptions extends RequestInit {
  responseType?: 'json' | 'blob';
  headers?: Record<string, string>;
  skipAuthRedirect?: boolean; // Option pour éviter la redirection automatique
}

export async function apiFetch(path: string, options?: ApiFetchOptions) {
  const { getAuthHeaders, logout } = useAuth();
  
  const headers: Record<string, string> = { ...(options?.headers ?? {}) };
  const authHeaders = getAuthHeaders();
  Object.assign(headers, authHeaders);
  
  // Ajouter Content-Type pour JSON si body est présent et non FormData
  if (options?.body && !(options.body instanceof FormData)) {
    if (!headers['Content-Type']) {
      headers['Content-Type'] = 'application/json';
    }
  }
  
  const init: RequestInit = {
    method: options?.method ?? 'GET',
    headers
  };
  
  if (options?.body) {
    init.body = options.body instanceof FormData 
      ? options.body 
      : JSON.stringify(options.body);
  }
  
  const res = await fetch(`${API_BASE}${path}`, init);
  
  if (!res.ok) {
    // Si erreur 401 (Unauthorized), déconnecter et rediriger vers login
    if (res.status === 401 && !options?.skipAuthRedirect) {
      await logout();
      // Vérifier qu'on n'est pas déjà sur la page de login pour éviter les boucles
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
      // Ne pas lancer d'erreur pour éviter d'afficher un message d'erreur
      // La redirection vers login est suffisante
      return Promise.reject(new Error('Session expirée. Veuillez vous reconnecter.'));
    }
    
    const message = await safeReadError(res);
    throw new Error(message ?? `Erreur API ${res.status}`);
  }
  
  if (options?.responseType === 'blob') {
    return res.blob();
  }
  
  return res.json();
}

async function safeReadError(response: Response) {
  try {
    const payload = await response.json();
    return payload?.message;
  } catch {
    return response.statusText;
  }
}

